import React, { useState, useEffect } from 'react'
import styles from './UnlimitedCodes.scss'
import { QRCodeContainer } from '../QRCodeContainer'
import { CircleSpinner } from 'common/spinners'
import { Icon } from 'common/Icon'
import { errorMessage } from 'lib/styleguide/base.scss'
import { Msg, useFormatMessage } from 'lib/localization'
import { CTA } from 'common/styleguide/CTA'
import { FormattedMessage as T, defineMessages } from 'react-intl'
import _ from 'lodash'

const messages = defineMessages({
  guestCode: {
    id: 'ShortcutQR.guestCode',
    defaultMessage: 'Guest code'
  },
  guestCodeTip: {
    id: 'ShortcutQR.guestCodeTip',
    defaultMessage: 'Expires user based on Experience setting'
  },
  guideCode: {
    id: 'ShortcutQR.guideCode',
    defaultMessage: 'Guide code'
  },
  guideCodeTip: {
    id: 'ShortcutQR.guideCodeTip',
    defaultMessage: 'Code is exempt from Experience expiration setting'
  },
  guestQR: {
    id: 'ShortcutQR.guestQR',
    defaultMessage: 'Guest QR'
  },
  guideQR: {
    id: 'ShortcutQR.guideQR',
    defaultMessage: 'Guide QR'
  },
  limitedMismatch: {
    id: 'UnlimitedCodes.limitedMismatch',
    defaultMessage: "To generate unlimited use codes please click 'Save'"
  }
})

export const UnlimitedCodes = ({
  experience,
  appCode,
  guideCode,
  updateCode,
  generateAppCode,
  hasGuideShortcut
}) => {
  const fm = useFormatMessage()
  return <div className={styles.middle}>
    <h3>
      <T
        id='UnlimitedCodes.createCode'
        defaultMessage='Create Unlimited Use Code' />
      {!experience.unlimited && <Icon style={{ marginLeft: '10px' }} size={26} i='ios-information-circle-outline' data-tip title={fm(messages.limitedMismatch)} /> }
    </h3>

    <T
      id='UnlimitedCodes.createOwnCode'
      defaultMessage='Create your own code, or use the access code below to invite guests to the Experience Guide.' />

    <div className={styles.flex}>
      <CodePicker
        appCode={appCode?.code}
        updateCode={updateCode}
        generateAppCode={generateAppCode}
        experience={experience}
        label={messages.guestCode}
        tip={messages.guestCodeTip}
      />

      {hasGuideShortcut && <CodePicker
        guideCodePicker
        appCode={guideCode?.code}
        updateCode={updateCode}
        generateAppCode={generateAppCode}
        experience={experience}
        label={messages.guideCode}
        tip={messages.guideCodeTip}
      />}
    </div>

    <hr />

    <div className={styles.bottom}>
      <h3>
        <T
          id='ShortcutQR.downloadQR'
          defaultMessage='Download QR code' />
      </h3>

      <T
        id='ShortcutQR.downloadQRText'
        defaultMessage='Download your custom QR code to invite guests to the Experience Guide.' />

      <div className={styles.qrCodes}>
        <QRCodeContainer
          experienceId={experience.id}
          appCode={appCode}
          label={messages.guestQR}
          tip={messages.guestCodeTip}
          disabled={!experience.unlimited} />
        {hasGuideShortcut && guideCode?.code && <QRCodeContainer
          experienceId={experience.id}
          appCode={guideCode}
          label={messages.guideQR}
          tip={messages.guideCodeTip}
          disabled={!experience.unlimited} />}
      </div>
    </div>
  </div>
}

const CodePicker = ({
  guideCodePicker,
  appCode,
  updateCode,
  generateAppCode,
  setAppCode,
  experience,
  label, // this is expected to be an intl message
  tip // this is expected to be an intl message
}) => {
  const [localCode, setLocalCode] = useState(appCode)
  const [requestPending, setRequestPending] = useState(false)
  const [codeInUse, setCodeInUse] = useState(false)

  const saveDisabled = requestPending || (localCode === appCode) || !experience.unlimited

  useEffect(() => {
    setLocalCode(appCode)
  }, [appCode])

  const saveCode = async e => {
    e.preventDefault()
    if (saveDisabled) { return }
    await setRequestPending(true)

    await updateCode(localCode, guideCodePicker)
      .catch(() => { setCodeInUse(true) })
      .finally(() => { setRequestPending(false) })
  }

  const generateNewCode = async () => {
    if (requestPending || !experience.unlimited) { return }
    await setRequestPending(true)

    await generateAppCode(guideCodePicker)
    setRequestPending(false)
  }

  const fm = useFormatMessage()
  return (
    <div className={styles.CodePicker}>
      <form onSubmit={saveCode}>
        <div className={styles.flex}>
          <div style={{ flexGrow: 1 }}>
            <span><Msg m={label} /> <Icon i='ios-information-circle-outline' data-tip title={fm(tip)} /></span>
            <div className={styles.flex}>
              <input disabled={!experience.unlimited} className={styles.codeInput} style={{ marginBottom: '0px' }} type='text' value={localCode ?? ''} onChange={e => setLocalCode(e.target.value)} />
              <CTA button type='submit' disabled={saveDisabled} margins={false}>
                {requestPending && <CircleSpinner size={14} /> }
                {!requestPending && localCode !== appCode && <span><T id='CodePicker.saveCode' defaultMessage='Save Code' /></span>}
                {!requestPending && localCode === appCode && <span><T id='CodePicker.saved' defaultMessage='Saved' /></span>}
              </CTA>
            </div>
          </div>
        </div>
        {codeInUse && <div className={errorMessage}>
          <T
            id='CodePicker.alreadyInUse'
            defaultMessage='<b>{localCode}</b> is already in use. Please choose a different code.'
            values={{
              b: (...c) => <b>{c}</b>,
              localCode
            }} />
        </div>}
      </form>
      <div className={styles.generate}>
        <CTA link margins={false} disabled={!experience.unlimited} href='#' onClick={generateNewCode} className={styles.generateUnlimited}>
          <T
            id='CodePicker.generate'
            defaultMessage='Generate New Unlimited Use {codeType}'
            values={{ codeType: _.startCase(fm(guideCodePicker ? messages.guideCode : messages.guestCode)) }}
          />
        </CTA>
      </div>
    </div>
  )
}
